import ErrorBoundary from '@components/ErrorBoundary';
import Spinner from '@components/spinner/Spinner';
import { useUpdateTheme } from '@hooks/postQueries';
import { Icon } from '@iconify/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import formatString from '@utils/formatString';
import { Suspense, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

function ThemeUpdateModal({
  openThemeModal,
  closeCategoryModal,
  themes,
  fetchThemesAndCategories,
  setOpenCodeModal,
  setSelectedCode,
  selectedNode,
  categories,
  id,
  getColorByCategory,
}) {
  let closeAllModalAfterSuccess = () => {
    closeCategoryModal();
    fetchThemesAndCategories();
  };
  const [isSummaryExpanded, setisSummaryExpanded] = useState(false);
  const { mutate: updateTheme, isPending } = useUpdateTheme(closeAllModalAfterSuccess);
  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      new_theme_name: formatString(selectedNode?.name ?? ''),
    },
  });

  useEffect(() => {
    setValue('new_theme_name', selectedNode?.name);
  }, [selectedNode]);

  const handleThemeUpdate = (data) => {
    let payload = {
      study_id: id,
      old_theme_name: selectedNode?.name,
      new_theme_name: data.new_theme_name,
    };
    updateTheme({
      payload,
    });
  };

  return (
    <Dialog open={openThemeModal} onClose={closeCategoryModal} size="md" maxWidth="800px">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <DialogTitle>
          <Typography variant="h6">Theme Details</Typography>
        </DialogTitle>

        <Box display={'flex'} bgcl gap={1.5} justifyContent={'end'} marginRight={'20px'}>
          <Button
            variant="custom"
            sx={{ ml: 'auto', cursor: 'pointer' }}
            onClick={closeCategoryModal}
          >
            Cancel
          </Button>
          <Button
            disabled={isPending}
            sx={{ cursor: 'pointer' }}
            type="submit"
            form="myForm"
            variant="contained"
          >
            {isPending ? 'Saving' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
      <Divider />
      <DialogContent
        sx={{ maxWidth: '900px', minWidth: '700px', position: 'relative', padding: '0' }}
      >
        <ErrorBoundary>
          <Suspense fallback={<Spinner />}>
            {themes
              .filter((theme) => theme.name === selectedNode?.name)
              .map((theme) => (
                <Box p={2} mb={2} key={theme.name}>
                  <form id="myForm" onSubmit={handleSubmit(handleThemeUpdate)}>
                    <Stack direction="row" alignItems="start" spacing={2} fullWidth>
                      <FormLabel sx={{ minWidth: 150 }}>Theme</FormLabel>
                      <Controller
                        name="new_theme_name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              '&:hover .edit-icon': {
                                opacity: 1,
                              },
                            }}
                          >
                            <TextField {...field} variant="outlined" size="small" fullWidth />
                            <IconButton
                              className="edit-icon"
                              sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                opacity: 0,
                                transition: 'opacity 0.3s',
                                padding: 0,
                              }}
                            >
                              <Icon icon="ri:edit-fill" width="24" color="#7d5ae8" height="24" />
                            </IconButton>
                          </Box>
                        )}
                      />
                    </Stack>
                  </form>

                  <Divider sx={{ mt: 1 }} />
                  <Typography mt={1}>
                    <span style={{ fontWeight: '600' }}>Definition : </span> {theme.description}
                  </Typography>

                  {theme?.summary && (
                    <Typography mt={0.5}>
                      <span style={{ fontWeight: '600' }}>Summary : </span>
                      <span>
                        {isSummaryExpanded ? theme?.summary : `${theme?.summary?.slice(0, 220)}...`}
                        <button
                          onClick={() => setisSummaryExpanded(!isSummaryExpanded)}
                          aria-expanded={isSummaryExpanded}
                          style={{
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            marginLeft: '10px',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                            color: '#7d5ae8',
                            textDecoration: 'underline',
                          }}
                        >
                          {isSummaryExpanded ? 'See Less' : 'See More'}
                        </button>
                      </span>
                    </Typography>
                  )}
                  <Typography mt={1}>
                    <span style={{ fontWeight: '600' }}>Saliency : </span> {selectedNode?.saliency}
                  </Typography>
                  <Divider sx={{ mt: 1 }} />

                  {theme.categories_included?.map((category) => {
                    const categoryData = categories[category];
                    return (
                      <Accordion
                        elevation={0}
                        sx={{ boxShadow: 'none' }}
                        defaultExpanded
                        key={category}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1-content"
                          id="panel1-header"
                        >
                          <Typography component="span">
                            <Typography fontSize={16} fontWeight={'500'}>
                              <span>Category : </span>
                              {formatString(category)} {`(n = ${categories[category]?.saliency})`}
                            </Typography>
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box key={category}>
                            <Typography>
                              <span style={{ fontWeight: '600' }}>Definition : </span>{' '}
                              {categoryData?.description}
                            </Typography>
                            {categoryData?.summary && (
                              <Typography mt={0.5}>
                                <span style={{ fontWeight: '600' }}>Summary : </span>{' '}
                                {categoryData?.summary}
                              </Typography>
                            )}
                            <Typography>
                              <span style={{ fontWeight: '600' }}>Saliency : </span>{' '}
                              {categories[category]?.saliency}
                            </Typography>

                            <Typography variant="subtitle2" mt={1}>
                              Codes:
                            </Typography>
                            <Box display={'flex'} gap={1} mt={1} flexWrap={'wrap'}>
                              {categories[category]?.codes_included.map((item) => (
                                <CodeChip
                                  code={item}
                                  key={item}
                                  definition={categories[category]?.description}
                                  saliency={categories[category]?.saliency}
                                  categoryKey={category}
                                  setOpenCodeModal={setOpenCodeModal}
                                  setSelectedCode={setSelectedCode}
                                  getColorByCategory={getColorByCategory}
                                />
                              ))}
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              ))}
          </Suspense>
        </ErrorBoundary>
      </DialogContent>
    </Dialog>
  );
}

export default ThemeUpdateModal;

const CodeChip = ({
  code,
  definition,
  saliency,
  categoryKey,
  setOpenCodeModal,
  setSelectedCode,
  getColorByCategory,
}) => (
  <Chip
    label={formatString(code)}
    sx={{
      backgroundColor: getColorByCategory(categoryKey),
      borderRadius: '4px',
      '& .MuiChip-root': {
        borderRadius: '4px',
      },
    }}
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      setOpenCodeModal(true);
      setSelectedCode({
        name: code,
        definition,
        location: 0,
        saliency,
        categoryKey,
      });
    }}
  />
);
